import { Link } from "react-router-dom";
import { Edit, Eye } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ListingImage from "./ListingImage";
import ListingStatusBadges from "./ListingStatusBadges";
import ListingStats from "./ListingStats";
import ListingActions from "./ListingActions";
import Button from "./Button";

interface ListingType {
  id: string;
  title: string;
  address: string;
  price: string;
  type: string;
  status: string;
  views: number;
  inquiries: number;
  image: string;
  featured: boolean;
  boosted: boolean;
  hasUnreadMessages?: boolean;
  hasUpdates?: boolean;
}

interface ListingCardProps {
  property: ListingType;
  onOpenFeaturedDialog: (id: string) => void;
  onOpenBoostDialog: (id: string) => void;
  onShareListing: (id: string, title: string) => void;
  onReportIssue: (id: string) => void;
  onNotificationToggle: (
    id: string,
    currentStatus: boolean | undefined
  ) => void;
}

const ListingCard = ({
  property,
  onOpenFeaturedDialog,
  onOpenBoostDialog,
  onShareListing,
  onReportIssue,
  onNotificationToggle,
}: ListingCardProps) => {
  return (
    <Card key={property.id} className="hover:shadow-md transition-all">
      <div className="flex flex-col sm:flex-row">
        <ListingImage property={property} />
        <div className="flex-grow flex flex-col">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-lg">{property.title}</CardTitle>
                <CardDescription>{property.address}</CardDescription>
              </div>
              <ListingStatusBadges property={property} />
            </div>
          </CardHeader>

          <CardContent className="pb-2">
            <div className="font-semibold text-lg mb-3">{property.price}</div>
            <ListingStats property={property} />
          </CardContent>

          <CardFooter className="flex justify-between flex-wrap gap-2 mt-auto">
            <ListingActions
              property={property}
              onOpenFeaturedDialog={onOpenFeaturedDialog}
              onOpenBoostDialog={onOpenBoostDialog}
              onShareListing={onShareListing}
              onReportIssue={onReportIssue}
              onNotificationToggle={onNotificationToggle}
            />

            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Button>
              <Button variant="default" size="sm">
                <Eye className="mr-2 h-4 w-4" />
                View
              </Button>
            </div>
          </CardFooter>
        </div>
      </div>
    </Card>
  );
};

export default ListingCard;
